import { formatHex, parse } from "culori";

/**
 * Convert any CSS colour the theme might hold into `#rrggbb`.
 *
 * Theme values arrive as `oklch(...)` (every styled preset), `var(--token)`
 * refs (the neutral presets), or plain hex once a user has touched a picker.
 *
 * This replaced a canvas round-trip. Neither `getComputedStyle().color` nor
 * canvas `fillStyle` can be scraped for this — both now preserve wide-gamut
 * colours as authored, so an `oklch()` stays in oklch form and pulling digits
 * out of it yields the function's first three numbers (a vivid magenta became
 * `#003a00`, a dark green). Rasterising to a 1×1 canvas worked but needed a
 * DOM; culori converts properly and runs anywhere, so the same code is usable
 * from tests.
 */
export function toHex(value: string, fallback = "#666666"): string {
  if (/^#[0-9a-f]{6}$/i.test(value)) return value.toLowerCase();

  // Resolve custom-property refs against the document root before converting.
  let resolved = value;
  if (value.startsWith("var(") && typeof document !== "undefined") {
    const token = value.slice(4, -1).trim();
    resolved =
      getComputedStyle(document.documentElement)
        .getPropertyValue(token)
        .trim() || value;
  }

  try {
    const parsed = parse(resolved);
    if (!parsed) return fallback;
    // Out-of-sRGB oklch values clamp rather than fail, which is what a hex
    // swatch needs — an unrepresentable colour still gets its nearest match.
    return formatHex(parsed) ?? fallback;
  } catch {
    return fallback;
  }
}
